import React, { FC, memo, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Doctor } from 'models/doctor';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons/lib';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
export interface BusinessPartnerDeleteDoctorButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartnerId: number;
  doctor: Doctor;
  status?: DeleteUserStatus;
  tooltip?: ReactNode;
  onDeleted?: () => void;
}

export const BusinessPartnerDeleteDoctorButton: FC<BusinessPartnerDeleteDoctorButtonProps> = memo(
  ({
    businessPartnerId,
    doctor,
    status = DeleteUserStatus.Archive,
    onDeleted,
    tooltip,
    ...buttonProps
  }) => {
    const [mutate, { isLoading }] = useMutation(
      BusinessPartnersAPI.deleteDoctor
    );
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title:
          DeleteUserStatus.Delete === status
            ? intlMessage(
                { id: 'entity.delete.confirmation' },
                {
                  name: `${doctor.firstName} ${doctor.lastName}`,
                }
              )
            : intlMessage(
                { id: 'entity.archive.confirmation' },
                {
                  name: `${doctor.firstName} ${doctor.lastName}`,
                }
              ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            { businessPartnerId, dto: { userId: doctor.userId, status } },
            {
              onSuccess: () => {
                message.success(
                  DeleteUserStatus.Delete === status
                    ? intlMessage(
                        { id: 'entity.delete.success' },
                        {
                          name: `${doctor.firstName} ${doctor.lastName}`,
                        }
                      )
                    : intlMessage(
                        { id: 'entity.archive.success' },
                        {
                          name: `${doctor.firstName} ${doctor.lastName}`,
                        }
                      )
                );
                onDeleted && onDeleted();
              },
            }
          ),
      });

    const button = (
      <Button
        id="button-delete-bp-doctor"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<WarningOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
